/**
 * Typing into a text layer.
 *
 * The keyboard belongs to a real, invisible <textarea> sitting over the layer.
 * That is not a shortcut - it is the only way to get an Arabic keyboard, an
 * IME, a phone's on-screen keyboard, autocorrect, clipboard, and
 * select-all-then-type to behave the way a customer already expects. Handling
 * raw keydown events instead looks simpler for a day and then fails for every
 * customer who is not typing English.
 *
 * So the textarea owns the text and the caret; the canvas owns what they look
 * like. This file is the bridge: it keeps the textarea's value in step with the
 * document, and its selection in step with the store, and draws nothing.
 */

import { useEffect, useLayoutEffect, useRef } from 'react';
import { useEditor } from '../state/store';
import { mmToPx } from '../model/units';
import type { TextLayer } from '../model/types';
import { findLayer } from '../model/types';
import { snapCaret } from '../text/layout';

export function TextEditor() {
  const field = useRef<HTMLTextAreaElement>(null);

  const editing = useEditor((s) => s.editing);
  const caret = useEditor((s) => s.caret);
  const view = useEditor((s) => s.view);
  const page = useEditor((s) => s.doc?.pages[s.pageIndex] ?? null);

  const layer = editing && page ? findLayer(page.layers, editing) : null;
  const text = layer && layer.type === 'text' ? layer : null;

  /* Focus follows the edit, and goes back to the canvas when it ends. */
  useEffect(() => {
    if (!text) return;

    const element = field.current;

    if (!element) return;

    element.focus({ preventScroll: true });
    element.setSelectionRange(caret.start, caret.end);
    // Only on entry - resetting the range on every caret change would fight
    // the customer's own arrow keys.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text?.id]);

  /**
   * The store's caret is authoritative when something else moved it.
   *
   * Clicking on the canvas sets it, and the textarea has to follow, but only
   * when the two have actually diverged - assigning a range the field already
   * has scrolls it in some browsers.
   */
  useLayoutEffect(() => {
    const element = field.current;

    if (!element || !text) return;

    if (element.selectionStart !== caret.start || element.selectionEnd !== caret.end) {
      element.setSelectionRange(caret.start, caret.end);
    }
  }, [caret.start, caret.end, text]);

  if (!text) return null;

  /**
   * Reads the caret back out of the field, and snaps it onto a real boundary.
   *
   * The textarea moves by code unit, which is not always somewhere a caret can
   * be: letters that shape together are one cluster, and a caret inside one has
   * no position on screen. Snapping in the direction of travel makes an arrow
   * key step over such a pair instead of vanishing into it - and writing the
   * snapped value back means the *next* arrow press starts from a real place.
   */
  const report = () => {
    const element = field.current;

    if (!element) return;

    // The layer as it is *now*, not as it was when this render started. Typing
    // updates the store synchronously and then calls this, so snapping against
    // the closed-over copy would measure the text as it was one keystroke ago:
    // its clusters end sooner, the caret gets dragged back inside the word, and
    // the next letter lands in the middle of the last one. Typing "Doha" came
    // out "Dhao".
    const state = useEditor.getState();
    const page = state.doc?.pages[state.pageIndex];
    const live = page ? findLayer(page.layers, text.id) : null;

    if (!live || live.type !== 'text') return;

    const was = state.caret;
    const start = element.selectionStart;
    const end = element.selectionEnd;

    const going = (from: number, to: number): 'forward' | 'back' | 'near' =>
      to > from ? 'forward' : to < from ? 'back' : 'near';

    const snapped = {
      start: snapCaret(live, start, going(was.start, start)),
      end: snapCaret(live, end, going(was.end, end)),
    };

    state.setCaret(snapped);

    if (snapped.start !== start || snapped.end !== end) {
      element.setSelectionRange(snapped.start, snapped.end);
    }
  };

  return (
    <textarea
      ref={field}
      className="tb-typing"
      value={text.text}
      spellCheck={false}
      autoCorrect="off"
      autoCapitalize="off"
      aria-label={`Editing ${text.name || 'text'}`}
      // Positioned over the layer so a phone scrolls the right thing into view
      // when its keyboard opens, and so the browser's own IME candidate window
      // appears next to the letters it is about.
      style={{
        left: view.x + mmToPx(text.x, view.zoom),
        top: view.y + mmToPx(text.y, view.zoom),
        width: Math.max(24, mmToPx(text.w, view.zoom)),
        height: Math.max(24, mmToPx(text.h, view.zoom)),
        transform: text.rotation ? `rotate(${text.rotation}deg)` : undefined,
      }}
      onChange={(event) => {
        const value = event.target.value;

        // One undo step per burst of typing, not one per character.
        useEditor
          .getState()
          .updateLayer(text.id, 'Type', (l) => ({ ...(l as TextLayer), text: value }), {
            coalesce: `type:${text.id}`,
          });

        report();
      }}
      onSelect={report}
      onKeyUp={report}
      onClick={report}
      onBlur={() => useEditor.getState().endEditing()}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          useEditor.getState().endEditing();
        }

        // Everything else is the textarea's business, including Enter: a text
        // layer is a paragraph, and a customer pressing Enter means a new line.
        event.stopPropagation();
      }}
    />
  );
}
